import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, CircularProgress, Drawer, Typography } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";

import { DistributorContext } from "../../../hooks/DistributorContext";
import { useStack } from "../../../hooks/useStack";
import { MemberContext } from "../../../hooks/MemberContext";
import { useAuth } from "../../../hooks/useAuth";
import { useSponsorType } from "../../../hooks/useSponsorType";
import { getSponsorUnicityId, getCustomerCategory } from "../../../common/helper-functions";
import { T } from "../../../hooks/useTranslate";
import { DistributorProfileDrawerContent } from "./DistributorProfileDrawerContent";
import { OrderSummaryDrawerContent } from "../../../components/drawer/OrderSummaryDrawerContent";
import { OrderDetailDrawerContent } from "../../../components/drawer/OrderDetailDrawerContent";
import { ReferralSummaryDrawerContent } from "../../../components/drawer/ReferralSummaryDrawerContent";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const drawerContentComponents = {
  profile: DistributorProfileDrawerContent,
  orders: OrderSummaryDrawerContent,
  orderDetails: OrderDetailDrawerContent,
  referrals: ReferralSummaryDrawerContent,
};


const DistributorsDrawer = () => {
  const { isDrawerOpen, setIsDrawerOpen, setDistributorData, resetContext: resetDistributorContext, } = useContext(DistributorContext);
  const { setMemberId, clearErrorMessage, distributorData, customerHrefData, errorMessage, resetContext: resetMemberContext } = useContext(MemberContext);
  const { userInfo } = useAuth();
  const [drawerContent, setDrawerContent] = useState(null);
  const [orderHref, setOrderHref] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const profileViewStack = useStack();

  const theme = useTheme();
  const viewportIsSmall = !useMediaQuery(theme.breakpoints.up("sm"));
  const DrawerContentComponent = drawerContentComponents[drawerContent];

  // GO-2724: this drawer (drawer=partner) had NO access control at all --
  // completely separate code path from MembersDrawer.jsx (drawer=member),
  // routed via components/Drawers.jsx, never touched by any prior round
  // tonight. Mirrors MembersDrawer.jsx's current logic exactly for
  // consistency: distributor-of-distributor / member-of-member unlimited
  // depth via type-matching, capped otherwise. See MembersDrawer.jsx for the
  // full reasoning on each piece below.
  const isPartnersPage = /^\/partners(\/|$)/.test(location.pathname);
  const memberSponsorId = getSponsorUnicityId(customerHrefData);
  const memberOwnType = getCustomerCategory(customerHrefData?.type);
  const sponsorKnown = Boolean(customerHrefData) && customerHrefData !== false && memberSponsorId !== undefined;
  const isDirectSponsee = sponsorKnown && memberSponsorId === userInfo?.unicityId?.toString();

  // Access rule, identical to MembersDrawer.jsx -- see the long comment there
  // for the reasoning and history. In short (GO-3345 round 2, PM 2026-08-05):
  //   * PARTNER targets: viewable at any depth, no sponsor lookup needed.
  //   * MEMBER targets: only the user's own frontline members, and members
  //     sponsored by one of those frontline members. Deeper is blocked
  //     regardless of the sponsor's account type, which replaces GO-2724's
  //     "member-of-member is unlimited depth" rule.
  // Both drawers must agree, because either can be pointed at either kind of
  // target -- drawer=partner vs drawer=member is chosen by the CALLER, from the
  // grid column that was clicked, not by what the account actually is.
  const targetIsMember = memberOwnType === "member";
  const needsSponsorLookup = sponsorKnown && targetIsMember && !isDirectSponsee;
  const { sponsorType, sponsorSponsorId, sponsorTypeKnown } = useSponsorType(
    needsSponsorLookup ? memberSponsorId : undefined
  );

  const isSponseeOfOwnMember =
    sponsorType === "member" &&
    sponsorSponsorId !== undefined &&
    sponsorSponsorId === userInfo?.unicityId?.toString();
  const memberViewable = isDirectSponsee || isSponseeOfOwnMember;

  const accessCheckResolved =
    sponsorKnown && (!needsSponsorLookup || sponsorTypeKnown);
  const isAccessDenied =
    isPartnersPage &&
    accessCheckResolved &&
    targetIsMember &&
    !memberViewable;
  // isAccessDenied is false both while genuinely allowed AND while still
  // loading (accessCheckResolved gates the whole expression to false, not
  // "unknown") -- a plain isAccessDenied ? deny : content ternary can't tell
  // those apart, so it renders content optimistically during the loading
  // window, a real profile briefly visible before the check flips to
  // denied. accessKnown gates content rendering on the check having
  // actually resolved either way.
  // If the profile fetch itself fails/401s, customerHrefData settles to
  // false and accessCheckResolved never resolves -- without this, the
  // drawer would spin forever with no error state. errorMessage is already
  // set by MemberContext on that failure; folding it in here lets
  // DrawerContentComponent render and hit its own existing `if
  // (errorMessage) return <MemberErrorScreen />` check in
  // DistributorProfileDrawerContent.jsx -- reusing that rather than
  // building a second error path. Safe: no member data is exposed by this,
  // the content component's own check intercepts first.
  const accessKnown = isPartnersPage ? accessCheckResolved || Boolean(errorMessage) : true;

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const drawer = searchParams.get("drawer");
    if (drawer === "partner") {
      setIsDrawerOpen(true);
      setDrawerContent(searchParams.get("drawerContent"));
      setMemberId(searchParams.get("id"));
      // push id onto profileViewStack if id is in URL and is not already in the stack
      if (searchParams.has("id") && !profileViewStack.contains(searchParams.get("id"))) {
        profileViewStack.push(searchParams.get("id"));
      }
      setOrderHref(searchParams.get("orderHref"));
    } else {
      setIsDrawerOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  useEffect(() => {
    if (Object.keys(distributorData).length > 0) {
      setDistributorData(distributorData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributorData])

  const handleClose = () => {
    navigate(location.pathname, { replace: true });
    setIsDrawerOpen(false);
    resetDistributorContext();
    resetMemberContext();
    clearErrorMessage();
    profileViewStack.clear();
  }

  return (
    <div>
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleClose}
      >
        <Box
          sx={{
            width: "calc(100vw - 16px)",
            maxWidth: !viewportIsSmall ? 400 : null,
            padding: 0,
          }}
          role="presentation"
        >
          {isAccessDenied ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "25px" }}>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: 40, gap: "15px", flexDirection: "column" }}>
                <ErrorIcon />
                <Typography><T>access_denied</T></Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  onClick={handleClose}
                  sx={{ height: "31px", color: "#FFFFFF", backgroundColor: "#009ECF", fontWeight: "600", fontSize: "12px", pl: "12px", pr: "12px", borderRadius: "4px", "&:hover": { backgroundColor: "rgba(0,158,198,0.48)" } }}
                >
                  Close
                </Button>
              </Box>
            </Box>
          ) : !accessKnown ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 500 }}>
              <CircularProgress data-testid="access-check-progress-bar" />
            </Box>
          ) : (
            DrawerContentComponent && (
              <DrawerContentComponent
                setIsDrawerOpen={setIsDrawerOpen}
                orderHref={orderHref}
                profileViewStack={profileViewStack}
              />
            )
          )}
        </Box>
      </Drawer>
    </div>
  );
};

export { DistributorsDrawer };
